'use client';

// The pending voice turn at the end of the transcript (BUILD 2 C2-8, WP2-8
// step 6): right-aligned like a user message and dimmed, with the undo
// countdown ring, Send now (Enter), Edit and × (Esc). No chat request is made
// inside the undo window; the controller sends at its end, on Send now, or
// never. A screen reader hears one polite announcement per pending turn, not
// every second of the countdown. The ring is still under reduced motion; the
// seconds are written beside it either way.
import { Button } from '@/components/ui/button';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import voiceCopy from '@/i18n/ns/voice';
import { cn } from '@/lib/utils';
import { PencilIcon, SendIcon, XIcon } from 'lucide-react';
import type { FC } from 'react';
import { useStore } from 'zustand';
import { useVoiceClock } from './clock';
import { voiceController, voiceStore } from './runtime';
import type { PendingView } from './store';
import { secondsLeft } from './voice-bar';

const RING_RADIUS = 9;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/** The share of the undo window still to run, 0..1 (1 while it waits for a tap). */
export function countdownShare(pending: PendingView, now: number): number {
  if (pending.deadline === null || pending.undoMs <= 0) return 1;
  return Math.max(0, Math.min(1, (pending.deadline - now) / pending.undoMs));
}

const CountdownRing: FC<{ pending: PendingView; now: number }> = ({ pending, now }) => {
  const share = countdownShare(pending, now);
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      data-slot="voice-pending-ring"
      className="text-primary size-6 shrink-0 -rotate-90"
    >
      <circle
        cx="12"
        cy="12"
        r={RING_RADIUS}
        className="stroke-border"
        strokeWidth="2.5"
        fill="none"
      />
      <circle
        cx="12"
        cy="12"
        r={RING_RADIUS}
        stroke="currentColor"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={RING_LENGTH}
        strokeDashoffset={RING_LENGTH * (1 - share)}
        className="transition-[stroke-dashoffset] duration-200 ease-linear motion-reduce:transition-none"
      />
    </svg>
  );
};

export function PendingVoiceBubble() {
  const copy = useNamespaceCopy(voiceCopy);
  const pending = useStore(voiceStore, (state) => state.pending);
  const announced = useStore(voiceStore, (state) => state.announced);
  const now = useVoiceClock(pending?.deadline != null);
  if (!pending) return null;
  const controller = voiceController();
  const counting = pending.deadline !== null;
  const text = pending.segments.map((segment) => segment.text).join(' ');
  return (
    <div
      data-slot="voice-pending"
      data-reason={pending.reason}
      role="group"
      aria-label={copy.pendingLabel}
      className="flex w-full flex-col items-end gap-1.5"
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) return;
        if (event.key === 'Escape') {
          // Only the pending turn: the window's Esc would also leave voice mode.
          event.preventDefault();
          event.stopPropagation();
          controller.discardPending();
        }
      }}
    >
      <div
        dir="auto"
        data-slot="voice-pending-text"
        className="bg-muted text-foreground max-w-[85%] rounded-3xl px-5 py-2.5 break-words whitespace-pre-wrap opacity-70"
      >
        {text}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1">
        {counting && <CountdownRing pending={pending} now={now} />}
        <span data-slot="voice-pending-status" className="text-muted-foreground px-1 text-xs">
          {counting
            ? formatCopy(copy.sendingIn, { seconds: secondsLeft(pending.deadline!, now) })
            : pending.reason === 'degraded'
              ? copy.degradedTapToSend
              : pending.reason === 'focus'
                ? copy.answerCardFirst
                : copy.tapToSend}
        </span>
        <Button
          type="button"
          variant="default"
          data-slot="voice-send-now"
          className="h-11 min-w-11 gap-1.5 rounded-full px-3"
          aria-keyshortcuts="Enter"
          onClick={() => controller.sendNow()}
        >
          <SendIcon className="size-4" aria-hidden="true" />
          <span>{copy.sendNow}</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          data-slot="voice-edit"
          className="h-11 min-w-11 gap-1.5 rounded-full px-3"
          onClick={() => controller.editPending()}
        >
          <PencilIcon className="size-4" aria-hidden="true" />
          <span>{copy.edit}</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-slot="voice-discard"
          className={cn('size-11 rounded-full')}
          aria-label={copy.discard}
          aria-keyshortcuts="Escape"
          title={copy.discard}
          onClick={() => controller.discardPending()}
        >
          <XIcon className="size-4" aria-hidden="true" />
        </Button>
      </div>
      <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        <span key={`pending-${announced}`}>{copy.pendingAnnouncement}</span>
      </span>
    </div>
  );
}
